import type { GameModel } from 'yage/game/GameModel';
import { AirportStaff } from '../components/index.js';
import { ENTRANCE } from '../domain/config.js';
import { restingBody, validateTerminalRecord, type StaffRecord } from '../domain/terminal-record.js';
import { shiftsActive } from '../domain/act2.js';
import { retainedAirports } from './retained.js';
import { assignStaffRecord, career, ids, make, state, stations } from './world.js';

const MOVABLE = ['clerk', 'crew', 'porter', 'cleaner', 'runner'];
/**
 * MGT-002 transfers between owned airports (Act II on). A transfer removes the body here before adding the record
 * there (never a duplicate): the helper gets a fresh identity at the destination, keeps skill, and arrives rested.
 */
export function transferOut(g: GameModel, recordId: string, airportId: string): string | null {
    if (!shiftsActive(state(g))) return 'Transfers open after the Promotion.';
    const id = ids(g, AirportStaff).find(s => g.getTypedUnsafe(AirportStaff, s).recordId === recordId);
    if (id === undefined) return 'That helper is not here.';
    const s = g.getTypedUnsafe(AirportStaff, id);
    if (!MOVABLE.includes(s.role) || s.training > 0) return 'That helper cannot move right now.';
    const records = retainedAirports(g), target = records.find(r => r.airportId === airportId);
    if (!target) return 'You do not own that airport.';
    const moved: StaffRecord = { id: `staff-${target.nextStaffId}`, role: s.role === 'clerk' ? 'crew' : s.role as StaffRecord['role'], stage: -1, homeStage: -1, task: 0, pilotIndex: null,
        body: restingBody(ENTRANCE.x, ENTRANCE.y, 2.1), ...(s.skill > 1 ? { condition: { skill: s.skill, energy: 100, training: 0, onBreak: false } } : {}) };
    const next = { ...target, nextStaffId: target.nextStaffId + 1, staff: [...target.staff, moved] };
    const checked = validateTerminalRecord(next, { people: new Set() });
    if (!checked.ok) return 'That airport cannot take another helper.';
    career(g).retainedAirports = JSON.stringify(records.map(r => r.airportId === airportId ? checked.record : r));
    if (s.homeStage >= 0 && !ids(g, AirportStaff).some(o => o !== id && g.getTypedUnsafe(AirportStaff, o).homeStage === s.homeStage)) stations(g)[s.homeStage].manager = false;
    g.removeEntity(id);
    state(g).revision++;
    return null;
}
/** Bring a helper from an owned airport to this one; they walk in through the front door. */
export function transferIn(g: GameModel, airportId: string, staffId: string): string | null {
    if (!shiftsActive(state(g))) return 'Transfers open after the Promotion.';
    const records = retainedAirports(g), source = records.find(r => r.airportId === airportId);
    const record = source?.staff.find(s => s.id === staffId);
    if (!source || !record || !MOVABLE.includes(record.role)) return 'That helper is not available.';
    const checked = validateTerminalRecord({ ...source, staff: source.staff.filter(s => s.id !== staffId) }, { people: new Set() });
    if (!checked.ok) return 'That airport cannot spare this helper.';
    career(g).retainedAirports = JSON.stringify(records.map(r => r.airportId === airportId ? checked.record : r));
    const id = make(g, 'Staff', { AirportStaff: { stage: -1, homeStage: -1, role: record.role, skill: record.condition?.skill ?? 1 }, Transform: { x: ENTRANCE.x, y: ENTRANCE.y }, AirportPath: { speed: 2.1 } });
    assignStaffRecord(g, id);
    state(g).revision++;
    return null;
}
